// apps/console/src/services/auth/config.ts
//
// Runtime auth config. Fetched ONCE from /config.json at app boot via
// loadAuthConfig() in main.tsx; subsequent callers use getAuthConfig() which
// returns the memoized value or throws if loadAuthConfig hasn't completed.
//
// Customer ops swaps /config.json on disk (volume-mounted via docker-compose
// in Plan 6) to switch the SPA between cloud (Cognito) and on-prem (Keycloak).
// Same SPA bundle, different config file.

import { setDeploymentCartoApiKey } from '../../config/carto'
import { setRuntimeApiBaseUrl } from '../../config/env'
import { ROUTES } from '../../routes/types'

/**
 * Fields shared by every provider variant of /config.json. `apiBaseUrl` is the
 * on-prem runtime API host override (read in loadAuthConfig); `liveChannelUrl`
 * is the realtime-broker WebSocket URL consumed by `useLiveChannel`;
 * `cartoApiKey` is the per-deployment CARTO basemap key consumed by
 * `config/carto.ts`. All are optional — cloud derives apiBaseUrl from the
 * hostname, a deploy that doesn't render liveChannelUrl simply leaves the
 * channel disconnected (the hook tolerates it gracefully, no crash), and one
 * that omits cartoApiKey keeps the watermarked keyless CARTO tiles.
 * `trackerIngestHost` / `trackerIngestPort` are the raw-TCP address GPS trackers
 * are told to send to (shown after registering one); both fall back — see
 * `getTrackerIngestHost` / `getTrackerIngestPort`.
 */
interface BaseRuntimeConfig {
  apiBaseUrl?: string
  liveChannelUrl?: string
  cartoApiKey?: string
  trackerIngestHost?: string
  trackerIngestPort?: number | string
}

/** The live-broker's Teltonika TCP port when a deployment does not say otherwise. */
export const DEFAULT_TRACKER_INGEST_PORT = 5027

export interface CognitoConfig extends BaseRuntimeConfig {
  provider: 'cognito'
  cognito: {
    region: string
    userPoolId: string
    clientId: string
  }
}

export interface KeycloakConfig extends BaseRuntimeConfig {
  provider: 'keycloak'
  keycloak: {
    authority: string // e.g. 'https://auth.customer.com/realms/gisify'
    clientId: string // e.g. 'gisify-spa'
  }
}

export type AuthConfig = CognitoConfig | KeycloakConfig

/**
 * Thrown by loadAuthConfig when /config.json is structurally unusable. main.tsx
 * catches every boot rejection and renders the "Configuration error" screen
 * with `err.message`, so the message must name the offending keys.
 */
export class AuthConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthConfigError'
  }
}

/**
 * Names of `keys` that are absent, non-string or blank on `block`. Reported as
 * `<blockName>.<key>` so the boot screen points at the exact /config.json path.
 */
function missingConfigKeys(blockName: string, block: unknown, keys: readonly string[]): string[] {
  const record = (block ?? {}) as Record<string, unknown>

  return keys
    .filter(key => {
      const value = record[key]

      return typeof value !== 'string' || value.trim() === ''
    })
    .map(key => `${blockName}.${key}`)
}

/**
 * The two routes mounted OUTSIDE `<AuthGuard>` in routes/index.tsx that a
 * signed-out visitor genuinely renders: the public form-fill surface and the
 * public dashboard viewer. Derived from ROUTES (rather than hardcoded) so a
 * path rename can't silently desync; both carry an `:id`, so the literal
 * prefix before the first param is what we match on.
 *
 * `/signup`, `/checkout` and `/auth/*` are public too but every one of them
 * drives an auth flow, so they stay on the strict path.
 */
const ANONYMOUS_ROUTE_PREFIXES = [ROUTES.FORMS_VIEW, ROUTES.DASHBOARD_VIEW].map(route =>
  route.slice(0, route.indexOf(':')),
)

function isAnonymousRoute(): boolean {
  if (typeof window === 'undefined') return false

  return ANONYMOUS_ROUTE_PREFIXES.some(prefix => window.location.pathname.startsWith(prefix))
}

let cached: AuthConfig | null = null
let inflight: Promise<AuthConfig> | null = null
let degraded: AuthConfigError | null = null

export async function loadAuthConfig(): Promise<AuthConfig> {
  if (cached) return cached
  if (inflight) return inflight

  inflight = (async (): Promise<AuthConfig> => {
    const res = await fetch('/config.json', { cache: 'no-store' })

    if (!res.ok) throw new Error(`/config.json fetch failed: ${res.status}`)
    const parsed = (await res.json()) as AuthConfig

    if (parsed.provider !== 'cognito' && parsed.provider !== 'keycloak') {
      throw new Error(
        `/config.json provider must be 'cognito' or 'keycloak'; got '${(parsed as { provider: string }).provider}'`,
      )
    }

    // The provider string alone isn't enough. A config.json with the right
    // provider but an empty/missing provider block used to boot cleanly and
    // then blow up much later at LOGIN with an opaque TypeError from
    // cognito-client (`cfg.cognito.region` off undefined) / keycloak-client
    // (`cfg.keycloak.authority`). Assert here — before `cached` is assigned —
    // exactly the keys those clients read verbatim, so a bad deploy surfaces as
    // main.tsx's "Configuration error" screen at boot. This is the behaviour
    // apps/console/docker-entrypoint.sh already documents ("empty strings cause
    // the SPA to surface clear 'config missing' errors") and mirrors
    // apps/license-admin/src/config/auth-config.ts.
    const missing =
      parsed.provider === 'cognito'
        ? missingConfigKeys('cognito', parsed.cognito, ['region', 'userPoolId', 'clientId'])
        : missingConfigKeys('keycloak', parsed.keycloak, ['authority', 'clientId'])

    if (missing.length > 0) {
      const configError = new AuthConfigError(
        `/config.json provider is '${parsed.provider}' but these keys are missing or empty: ${missing.join(', ')}`,
      )

      // ...but ONLY for routes that sign a user in. The public form-fill and
      // public dashboard-view pages never touch the provider, and they served
      // fine before this validation existed — an on-prem operator who starts
      // the frontend container without OIDC_ISSUER gets `authority: ''` from
      // docker-entrypoint.sh, and hard-failing boot would take those two pages
      // down with the login page. Degrade there instead: mount the SPA and
      // remember why the config is unusable so getAuthConfigError() can report
      // it to anything that does need a working provider.
      if (!isAnonymousRoute()) throw configError

      degraded = configError
      // eslint-disable-next-line no-console
      console.error('[auth-config] degraded — sign-in is unavailable on this deployment:', configError.message)
    }

    // Optional runtime API base. On-prem the API host is per-deployment
    // (https://api.${ROOT_DOMAIN}) and can't be derived from the SPA's hostname
    // — the docker entrypoint writes it into config.json. Cloud omits it and
    // keeps the build-time derived value. Wire it into the api client now,
    // before React mounts, so the first authed call uses the right host.
    const apiBaseUrl = (parsed as { apiBaseUrl?: unknown }).apiBaseUrl

    if (typeof apiBaseUrl === 'string' && apiBaseUrl.trim()) {
      setRuntimeApiBaseUrl(apiBaseUrl)
    }

    // Optional per-deployment CARTO basemap key. Seeded before React mounts so
    // the first basemap render already carries it; an org-level key (on-prem
    // only) overrides it later via BrandingProvider. Absent → keyless tiles,
    // which CARTO now watermarks but still serves.
    const cartoApiKey = (parsed as { cartoApiKey?: unknown }).cartoApiKey

    if (typeof cartoApiKey === 'string' && cartoApiKey.trim()) {
      setDeploymentCartoApiKey(cartoApiKey)
    }

    cached = parsed

    return cached
  })().finally(() => {
    inflight = null
  })

  return inflight
}

export function getAuthConfig(): AuthConfig {
  if (!cached)
    throw new Error('getAuthConfig() called before loadAuthConfig() resolved — wait for the boot promise in main.tsx')

  return cached
}

/**
 * The AuthConfigError that WOULD have failed boot, when /config.json's provider
 * block is unusable but the SPA was loaded on one of the anonymous routes and
 * mounted anyway. `null` on a healthy config. Surfaces on any sign-in path so a
 * misconfigured deployment reports the offending keys instead of failing
 * opaquely deep inside cognito-client / keycloak-client.
 */
export function getAuthConfigError(): AuthConfigError | null {
  return degraded
}

/**
 * The realtime-broker WebSocket URL from /config.json, or null when the config
 * hasn't loaded yet / a deploy didn't render the field. Never throws — callers
 * (e.g. useLiveChannel) treat null as "no channel; stay disconnected".
 */
export function getLiveChannelUrl(): string | null {
  const url = cached?.liveChannelUrl

  return typeof url === 'string' && url.trim() ? url.trim() : null
}

/**
 * The host a GPS tracker is told to connect to (the "Domain" field in Teltonika Configurator).
 *
 * `trackerIngestHost` from /config.json when set; otherwise the hostname of `liveChannelUrl`, because
 * the Teltonika listener runs inside the same live-broker that serves the WebSocket. Null when
 * neither is usable — the registration dialog then says the address is unknown rather than inventing
 * one a device would be pointed at for nothing.
 */
export function getTrackerIngestHost(): string | null {
  const explicit = cached?.trackerIngestHost

  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim()

  const live = getLiveChannelUrl()

  if (!live) return null

  try {
    const hostname = new URL(live).hostname

    return hostname || null
  } catch {
    return null
  }
}

/** The TCP port a GPS tracker sends to: `trackerIngestPort` when a valid port, else 5027. */
export function getTrackerIngestPort(): number {
  const raw = cached?.trackerIngestPort
  const port = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw

  return typeof port === 'number' && Number.isInteger(port) && port >= 1 && port <= 65535
    ? port
    : DEFAULT_TRACKER_INGEST_PORT
}

/** Test-only — resets the memoized config so each test starts fresh. */
export function __resetAuthConfigForTests(): void {
  cached = null
  inflight = null
  degraded = null
}
